import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import { Link } from "react-router-dom";
import Button from "../components/Button.js";
import ImageGallery from "../components/ImageGallery";
import Loader from "../components/Loader";
import { CacheContext } from "../contexts/CacheContext";
import "../styles/_ImageGallery.scss";

const Home = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showButton, setShowButton] = useState(false);
  const sentinelRef = useRef(null);
  const { cache, setCachedData } = useContext(CacheContext);

  // State to track which images are visible for animation
  const [visibleImageIds, setVisibleImageIds] = useState(new Set());
  const observer = useRef(null);

  // Callback to set up IntersectionObserver
  const setupObserver = useCallback(() => {
    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleImageIds((prev) =>
              new Set(prev).add(entry.target.dataset.imageId)
            );
            observer.current.unobserve(entry.target); // Stop observing once visible
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: "0px",
        threshold: 0.1, // Trigger when 10% of the item is visible
      }
    );
  }, []);

  // Callback to set ref for each image item
  const setItemRef = useCallback((el, imageId) => {
    if (el && observer.current) {
      observer.current.observe(el);
    }
  }, []);

  useEffect(() => {
    const fetchImages = async () => {
      if (cache.homeImages) {
        setImages(cache.homeImages);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `https://api.unsplash.com/photos/random?count=15&client_id=xIVYxkrz6cl6LjI_THMykrl0yDcXP9UQWt6SdM2ydEM`
        );
        if (!response.ok) throw new Error(`HTTP error! ${response.status}`);
        const data = await response.json();
        setImages(data);
        setCachedData("homeImages", data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [cache.homeImages, setCachedData]);

  useEffect(() => {
    // Re-setup observer when images change
    setupObserver();
    // Observe all image gallery items
    document.querySelectorAll(".image-gallery-item").forEach((item) => {
      observer.current.observe(item);
    });

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [images, setupObserver]); // Depend on images and setupObserver

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) setShowButton(true);
        else setShowButton(false);
      },
      {
        root: null,
        threshold: 0.1,
      }
    );

    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, []);

  if (loading) return <Loader />;
  if (error) return <div className="home-page">Error: {error.message}</div>;

  return (
    <div className="home-page">
      <ImageGallery
        images={images}
        visibleImageIds={visibleImageIds}
        setItemRef={setItemRef}
      />

      <div ref={sentinelRef} style={{ height: "60px" }}></div>
      <Button large to="/gallery">
        See More
      </Button>
    </div>
  );
};

export default Home;
